/* Personal Finance, RBAC, Audit, Templates, Transaction modal */
const { useState: useStateF, useEffect: useEffectF } = React;

// ============ Personal Finance ============
function FinancePage({ go }) {
  const [tab, setTab] = useStateF('all');
  const [selected, setSelected] = useStateF(null);
  const isAdmin = window.QH_DATA?.activeRole?.id === 'admin';

  const filtered = QH_DATA.financeApps.filter(a => tab === 'all' || a.status === tab);
  const counts = {
    all: QH_DATA.financeApps.length,
    draft: QH_DATA.financeApps.filter(a => a.status === 'draft').length,
    pending: QH_DATA.financeApps.filter(a => a.status === 'pending').length,
    approved: QH_DATA.financeApps.filter(a => a.status === 'approved').length,
    rejected: QH_DATA.financeApps.filter(a => a.status === 'rejected').length,
  };
  const totalApproved = QH_DATA.financeApps.filter(a => a.status === 'approved').reduce((s, x) => s + x.amount, 0);
  const totalRepaid = QH_DATA.repayments.filter(r => r.status === 'cleared').reduce((s, x) => s + x.amount, 0);
  const totalPending = QH_DATA.repayments.filter(r => r.status === 'pending' || r.status === 'overdue' || r.status === 'bounced').reduce((s, x) => s + x.amount, 0);

  const reopenDraft = async (app) => {
    if (!window.confirm(`Reopen ${app.id.toUpperCase()} as a draft? Status flips back to "draft" and the operator can edit every step.`)) return;
    try {
      await window.QH_API.post(`/finance/applications/${app.id}/reopen-draft`);
      await window.QH_STORE.reload();
    } catch (e) { alert(e.message || 'Reopen failed.'); }
  };

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb"><span>Personal Finance</span></div>
          <h1>Personal Finance</h1>
          <div className="subtitle">{counts.pending} pending · {counts.draft} drafts · {QH_DATA.defaulters.length} defaulters</div>
        </div>
        <div className="page-header-actions">
          <button className="btn" onClick={() => window.QH_STORE.reload()} title="Reload"><I.Refresh/></button>
          <button className="btn" onClick={() => window.safeDownload('/finance/applications/export.csv', 'finance-apps.csv')}><I.Download/> Export</button>
          {window.can('finance_apps','create') && (
            <button className="btn primary" onClick={() => go && go('financeWizard:new')}><I.Plus/> New application</button>
          )}
        </div>
      </div>

      <div className="page-body" style={{display: 'flex', flexDirection: 'column', gap: 12}}>
        <KPIStrip items={[
          { label: 'Total approved', value: fmtBHD(totalApproved) + ' BHD', sub: `${counts.approved} active applications` },
          { label: 'Cleared repayments', value: fmtBHD(totalRepaid) + ' BHD', sub: 'lifetime' },
          { label: 'Pending repayments', value: fmtBHD(totalPending) + ' BHD', sub: '' },
          { label: 'Defaulters', value: QH_DATA.defaulters.length, sub: fmtBHD(QH_DATA.defaulters.reduce((s, x) => s + x.dueAmount, 0)) + ' BHD outstanding' },
        ]}/>

        <div className="tabs" style={{borderRadius: 'var(--radius) var(--radius) 0 0', border: '1px solid var(--line)', borderBottom: 'none'}}>
          {[['all', 'All'], ['draft', 'Drafts'], ['pending', 'Pending'], ['approved', 'Approved'], ['rejected', 'Rejected']].map(([k, l]) => (
            <button key={k} className={`tab ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>{l}<span className="count">{counts[k]}</span></button>
          ))}
        </div>

        <div className="table-wrap" style={{borderTopLeftRadius: 0, borderTopRightRadius: 0, marginTop: -12}}>
          <table className="data">
            <thead><tr>
              <th>Ref</th><th>Applicant</th><th>Purpose</th>
              <th className="right">Applied</th>
              <th className="right">Approved</th>
              <th>Tenure</th><th>Date</th><th>Officer</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {filtered.map(a => {
                const isDraft = a.status === 'draft';
                const isFinal = a.status === 'approved' || a.status === 'rejected';
                return (
                  <tr key={a.id} className="clickable"
                      onClick={() => isDraft ? go && go('financeWizard:' + a.id) : go && go('financeApp:' + a.id)}>
                    <td className="font-mono text-xs">{a.id.toUpperCase()}</td>
                    <td>{a.applicant}</td>
                    <td><span className="tag" title={a.purpose === 'other' && a.purposeOther ? a.purposeOther : ''}>{window.formatPurpose ? window.formatPurpose(a.purpose, a.purposeOther) : a.purpose}</span></td>
                    <td className="num right">{fmtBHD(a.appliedAmount ?? a.amount)}</td>
                    <td className="num right" style={{fontWeight: 600}}>{a.status === 'rejected' ? '—' : fmtBHD(a.amount)}</td>
                    <td className="num">{a.tenure}m</td>
                    <td className="font-mono text-xs">{a.applied}</td>
                    <td className="text-xs">{a.officer}</td>
                    <td>
                      <span className={`tag ${a.status === 'approved' ? 'pos' : a.status === 'rejected' ? 'neg' : a.status === 'draft' ? '' : 'warn'}`}>
                        <span className="dot"/>{a.status}
                      </span>
                    </td>
                    <td style={{whiteSpace:'nowrap'}}>
                      {isDraft && (
                        <button className="btn sm primary" onClick={(e) => { e.stopPropagation(); go && go('financeWizard:' + a.id); }}>Resume</button>
                      )}
                      {isFinal && isAdmin && (
                        <button className="btn sm" onClick={(e) => { e.stopPropagation(); reopenDraft(a); }} title="Admin: flip back to draft for corrections">Reopen</button>
                      )}
                      {!isDraft && !isFinal && <I.ChevronRight s={12}/>}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={10} style={{textAlign:'center',padding:24,color:'var(--ink-3)',fontSize:12}}>No applications in this tab.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
          <RepaymentsPanel/>
          <DefaultersPanel/>
        </div>
      </div>

      {selected && <FinanceDetailModal app={selected} onClose={() => setSelected(null)}/>}
    </>
  );
}

function FinanceApplyModal({ onClose }) {
  const [step, setStep] = useStateF(1);
  const [member, setMember] = useStateF(null);
  const [purpose, setPurpose] = useStateF('business');
  const [amount, setAmount] = useStateF('5000.000');
  const [tenure, setTenure] = useStateF('12');
  const [notes, setNotes] = useStateF('');
  const [busy, setBusy] = useStateF(false);
  const [err, setErr] = useStateF('');

  const isDefaulter = member && QH_DATA.defaulters.some(d => d.memberItsNo === member.itsNo);

  const submitApp = async () => {
    if (!member) { setErr('Pick a member.'); return; }
    if (isDefaulter) { setErr('Member is a defaulter — settle outstanding dues first.'); return; }
    setBusy(true); setErr('');
    try {
      await window.QH_API.post('/finance/applications', {
        memberItsNo: member.itsNo, applicant: member.fullName,
        purpose, amount: Number(amount), tenure: Number(tenure), notes: notes || null,
      });
      await window.QH_STORE.reload();
      onClose();
    } catch (e) { setErr(e.message || 'Failed to submit application.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h3>New finance application</h3><button className="icon-btn close" onClick={onClose}><I.X/></button></div>
        <div className="modal-body">
          <div className="stepper">
            {['Applicant', 'Purpose & amount', 'Review'].map((l, i) => (
              <React.Fragment key={i}>
                <div className={`step ${step === i + 1 ? 'active' : step > i + 1 ? 'done' : ''}`}>
                  <span className="step-num">{step > i + 1 ? <I.Check s={11}/> : i + 1}</span>{l}
                </div>
                {i < 2 && <div className="step-line"/>}
              </React.Fragment>
            ))}
          </div>
          {step === 1 && <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
            <div className="field" style={{gridColumn: '1 / -1'}}>
              <label>Member (ITS / CPR / name)</label>
              <MemberPicker value={member} onChange={setMember} autoFocus/>
            </div>
            {member && <>
              <div className="field"><label>ITS No.</label><input className="input font-mono" value={member.itsNo} readOnly/></div>
              <div className="field"><label>CPR</label><input className="input font-mono" value={member.cpr || ''} readOnly/></div>
              <div className="field"><label>Mobile</label><input className="input font-mono" value={member.mobile1 || ''} readOnly/></div>
              <div className="field"><label>Email</label><input className="input" value={member.email1 || ''} readOnly/></div>
              <div style={{gridColumn:'1 / -1',padding:10, borderRadius:4, fontSize:12, display:'flex', alignItems:'center', gap:8,
                          background: isDefaulter ? 'var(--neg-soft)' : 'var(--info-soft)',
                          color: isDefaulter ? 'var(--neg)' : 'var(--info)'}}>
                {isDefaulter ? <><I.AlertCircle s={12}/> Member is a defaulter — application will be rejected.</> : <><I.Check s={12}/> No defaulter flag · Eligible to apply</>}
              </div>
            </>}
          </div>}
          {step === 2 && <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
            <div className="field" style={{gridColumn: '1 / -1'}}>
              <label>Purpose</label>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6}}>
                {['business', 'house', 'medical', 'other'].map(p => (
                  <button key={p} type="button" onClick={() => setPurpose(p)} className={`btn ${purpose === p ? 'accent' : ''}`} style={{justifyContent: 'center', textTransform: 'capitalize', height: 36}}>{p}</button>
                ))}
              </div>
            </div>
            <div className="field"><label>Requested amount (BHD)</label><input className="input font-mono" value={amount} onChange={(e) => setAmount(e.target.value)}/></div>
            <div className="field"><label>Repayment tenure (months)</label><input className="input font-mono" value={tenure} onChange={(e) => setTenure(e.target.value)}/></div>
            <div className="field" style={{gridColumn: '1 / -1'}}><label>Notes</label><textarea className="textarea" placeholder="Optional notes for the approver…" value={notes} onChange={e => setNotes(e.target.value)}/></div>
            <div style={{gridColumn: '1 / -1', padding: 12, background: 'var(--bg-sunken)', borderRadius: 4, fontSize: 12, display: 'flex', justifyContent: 'space-between'}}>
              <span className="muted">Estimated monthly installment</span>
              <span className="font-mono" style={{fontWeight: 600}}>{fmtBHD(Number(amount) / Number(tenure || 1))} BHD</span>
            </div>
          </div>}
          {step === 3 && <div>
            <div className="card">
              <div className="card-header">Application summary</div>
              <div className="card-body" style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13}}>
                <div><div className="muted text-xs uppercase">Member</div><div>{member?.fullName} <span className="muted font-mono text-xs">({member?.itsNo})</span></div></div>
                <div><div className="muted text-xs uppercase">Purpose</div><div style={{textTransform: 'capitalize'}}>{purpose}</div></div>
                <div><div className="muted text-xs uppercase">Amount</div><div className="font-mono" style={{fontWeight: 600}}>{fmtBHD(amount)} BHD</div></div>
                <div><div className="muted text-xs uppercase">Tenure</div><div className="font-mono">{tenure} months</div></div>
                <div><div className="muted text-xs uppercase">Monthly</div><div className="font-mono">{fmtBHD(Number(amount) / Number(tenure || 1))} BHD</div></div>
                <div><div className="muted text-xs uppercase">Officer</div><div>{QH_DATA.currentUser?.name || ''}</div></div>
              </div>
            </div>
            <div style={{padding: 10, background: 'var(--info-soft)', color: 'var(--info)', borderRadius: 4, fontSize: 12, marginTop: 12, display: 'flex', alignItems: 'center', gap: 8}}>
              <I.AlertCircle s={12}/> Gold security and guarantor cheques are collected at approval, not at apply time.
            </div>
          </div>}
          {err && <div style={{padding:'8px 14px',color:'var(--neg)',fontSize:12}}>{err}</div>}
        </div>
        <div className="modal-footer">
          {step > 1 && <button className="btn" onClick={() => setStep(step - 1)}>Back</button>}
          <div style={{flex: 1}}/>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          {step < 3 ? <button className="btn primary" onClick={() => setStep(step + 1)} disabled={step === 1 && !member}>Continue<I.ArrowRight/></button> :
            <button className="btn accent" disabled={busy || !member || isDefaulter} onClick={submitApp}>{busy ? 'Submitting…' : 'Submit for approval'}<I.Check/></button>}
        </div>
      </div>
    </div>
  );
}

function FinanceDetailModal({ app, onClose }) {
  const [busy, setBusy] = useStateF(false);
  const [showApprove, setShowApprove] = useStateF(false);
  const [detail, setDetail] = useStateF(null);

  // Pull the full record (including persisted guarantor cheques) when opened.
  useEffectF(() => {
    let cancel = false;
    (async () => {
      try {
        const d = await window.QH_API.get(`/finance/applications/${app.id}`);
        if (!cancel) setDetail(d);
      } catch {}
    })();
    return () => { cancel = true; };
  }, [app.id]);

  const reject = async () => {
    const reason = window.prompt('Reason for rejection?', 'Insufficient collateral');
    if (!reason) return;
    setBusy(true);
    try {
      await window.QH_API.post(`/finance/applications/${app.id}/reject`, { reason });
      await window.QH_STORE.reload();
      setTimeout(onClose, 200);
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  const cheques = detail?.guarantorCheques || [];
  const chequesTotal = cheques.reduce((s, c) => s + Number(c.amount || 0), 0);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3>{app.applicant} <span className="muted font-mono text-sm" style={{marginLeft: 6}}>{app.id.toUpperCase()}</span></h3>
            <div className="text-xs muted" style={{marginTop: 2}}>{window.formatPurpose ? window.formatPurpose(app.purpose, app.purposeOther) : app.purpose} finance · {app.tenure} months · {app.officer}{app.memberItsNo ? ` · ITS ${app.memberItsNo}` : ''}</div>
          </div>
          <button className="icon-btn close" onClick={onClose} style={{marginLeft: 'auto'}}><I.X/></button>
        </div>
        <div className="modal-body">
          <KPIStrip items={[
            { label: 'Requested', value: fmtBHD(app.amount), sub: 'BHD' },
            { label: 'Tenure', value: app.tenure + 'm' },
            { label: 'Monthly', value: fmtBHD(app.amount / app.tenure), sub: 'BHD / month' },
          ]}/>
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12}}>
            <div className="card"><div className="card-header">Gold security</div>
              <div className="card-body">
                <div className="font-mono" style={{fontSize: 22, fontWeight: 600}}>{app.collateral ? fmtBHD(app.collateral.gold) : fmtBHD(0)} BHD</div>
                <div className={`text-xs ${app.collateral?.gold === app.amount ? 'pos' : 'muted'}`} style={{marginTop: 4}}>
                  {app.collateral?.gold === app.amount ? <><I.Check s={11}/> Matches approved amount</> : 'Required to equal amount on approval'}
                </div>
              </div>
            </div>
            <div className="card"><div className="card-header">Guarantor cheques</div>
              <div className="card-body">
                <div className="font-mono" style={{fontSize: 22, fontWeight: 600}}>{fmtBHD(chequesTotal)} BHD</div>
                <div className={`text-xs ${chequesTotal === app.amount ? 'pos' : 'muted'}`} style={{marginTop: 4}}>
                  {cheques.length} cheque(s) {chequesTotal === app.amount ? <><I.Check s={11}/> Total matches</> : '— required to equal amount'}
                </div>
              </div>
            </div>
          </div>
          {cheques.length > 0 && <>
            <div className="text-xs uppercase muted" style={{margin: '14px 0 6px'}}>Cheques on file</div>
            <div className="table-wrap">
              <table className="data">
                <thead><tr><th>Guarantor</th><th>Cheque #</th><th>Bank</th><th>Date</th><th className="right">Amount · BHD</th><th>Status</th></tr></thead>
                <tbody>
                  {cheques.map(c => (
                    <tr key={c.id}>
                      <td>{c.guarantorName}</td>
                      <td className="font-mono text-xs">{c.chequeNumber || '—'}</td>
                      <td>{c.bank || '—'}</td>
                      <td className="font-mono text-xs">{c.chequeDate}</td>
                      <td className="num right" style={{fontWeight: 600}}>{fmtBHD(c.amount)}</td>
                      <td><span className={`tag ${c.status === 'released' ? 'pos' : c.status === 'bounced' ? 'neg' : 'info'}`}>{c.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>}
          <div className="divider"/>
          <div className="text-xs uppercase muted" style={{marginBottom: 8}}>Application timeline</div>
          <div className="timeline">
            <div className="timeline-item"><div className="timeline-icon"><I.Plus s={11}/></div><div><div style={{fontSize: 12}}><b>Application submitted</b></div><div className="text-xs muted font-mono">{app.applied}</div></div></div>
            {app.status === 'approved' && <div className="timeline-item"><div className="timeline-icon" style={{background: 'var(--pos-soft)', color: 'var(--pos)'}}><I.Check s={11}/></div><div><div style={{fontSize: 12}}><b>Approved by {app.officer}</b></div><div className="text-xs muted">Security verified · Cheques on file</div><div className="text-xs muted font-mono">{app.approvedOn}</div></div></div>}
            {app.status === 'rejected' && <div className="timeline-item"><div className="timeline-icon" style={{background: 'var(--neg-soft)', color: 'var(--neg)'}}><I.X s={11}/></div><div><div style={{fontSize: 12}}><b>Rejected</b></div><div className="text-xs muted">{app.reason}</div><div className="text-xs muted font-mono">{app.rejectedOn}</div></div></div>}
            {app.status === 'pending' && <div className="timeline-item"><div className="timeline-icon" style={{background: 'var(--warn-soft)', color: 'var(--warn)'}}><I.AlertCircle s={11}/></div><div><div style={{fontSize: 12}}><b>Awaiting approval</b></div><div className="text-xs muted">Assigned to {app.officer}</div></div></div>}
          </div>
        </div>
        {app.status === 'pending' && <div className="modal-footer">
          <button className="btn danger" disabled={busy} onClick={reject}><I.X/> Reject</button>
          <div style={{flex: 1}}/>
          <button className="btn" disabled={busy} onClick={async () => { const note = window.prompt('What information do you need?', ''); if (!note) return; try { await window.QH_API.post(`/finance/applications/${app.id}/request-info`, { reason: note }); await window.QH_STORE.reload(); alert('Note added.'); } catch (e) { alert(e.message); } }}><I.MessageSquare s={12}/> Request more info</button>
          <button className="btn accent" disabled={busy} onClick={() => setShowApprove(true)}><I.Check/> Approve…</button>
        </div>}
      </div>
      {showApprove && <ApproveModal app={app} onClose={() => setShowApprove(false)} onApproved={() => { setShowApprove(false); onClose(); }}/>}
    </div>
  );
}

// Approval flow: collect gold value (must equal amount) + multiple guarantor
// cheques (must sum to amount). Server enforces both checks.
function ApproveModal({ app, onClose, onApproved }) {
  const defaultBank = (QH_DATA.banks || []).find(b => b.active)?.name || '';
  const [gold, setGold] = useStateF(String(app.amount));
  const [cheques, setCheques] = useStateF([
    { guarantorName: '', amount: String(app.amount), chequeDate: new Date().toISOString().slice(0, 10), chequeNumber: '', bank: defaultBank },
  ]);
  const [busy, setBusy] = useStateF(false);
  const [err, setErr] = useStateF('');

  const totalCheques = cheques.reduce((s, c) => s + Number(c.amount || 0), 0);
  const goldOk = Number(gold) === Number(app.amount);
  const chequesOk = totalCheques === Number(app.amount);

  const updateCheque = (i, k, v) => setCheques(arr => arr.map((c, idx) => idx === i ? {...c, [k]: v} : c));
  const addCheque = () => setCheques(arr => [...arr, { guarantorName: '', amount: '0.000', chequeDate: new Date().toISOString().slice(0, 10), chequeNumber: '', bank: defaultBank }]);
  const removeCheque = (i) => setCheques(arr => arr.filter((_, idx) => idx !== i));

  const submit = async () => {
    setErr('');
    if (!goldOk) { setErr(`Gold security must equal ${app.amount} BHD.`); return; }
    if (!chequesOk) { setErr(`Cheque total (${fmtBHD(totalCheques)}) must equal ${fmtBHD(app.amount)} BHD.`); return; }
    for (const c of cheques) {
      if (!c.guarantorName?.trim()) { setErr('Each cheque needs a guarantor name.'); return; }
      if (!(Number(c.amount) > 0)) { setErr('Each cheque amount must be positive.'); return; }
    }
    setBusy(true);
    try {
      await window.QH_API.post(`/finance/applications/${app.id}/approve`, {
        gold: Number(gold),
        cheques: cheques.map(c => ({
          guarantorName: c.guarantorName.trim(),
          amount: Number(c.amount),
          chequeDate: c.chequeDate,
          chequeNumber: c.chequeNumber || null,
          bank: c.bank || null,
        })),
      });
      await window.QH_STORE.reload();
      onApproved();
    } catch (e) { setErr(e.message || 'Approval failed.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>Approve {app.id.toUpperCase()}</h3><button className="icon-btn close" onClick={onClose}><I.X/></button></div>
        <div className="modal-body" style={{display:'flex',flexDirection:'column',gap:12}}>
          <div style={{padding:10,background:'var(--info-soft)',color:'var(--info)',borderRadius:4,fontSize:12}}>
            Gold security must equal <b>{fmtBHD(app.amount)} BHD</b>. Sum of guarantor cheques must equal <b>{fmtBHD(app.amount)} BHD</b>.
          </div>
          <div className="field"><label>Gold security (BHD)</label>
            <input className="input font-mono" value={gold} onChange={e => setGold(e.target.value)}/>
            <div className={`text-xs ${goldOk ? 'pos' : 'muted'}`} style={{marginTop:4}}>{goldOk ? <><I.Check s={11}/> matches</> : `must equal ${fmtBHD(app.amount)}`}</div>
          </div>
          <div>
            <div style={{display:'flex',alignItems:'center',marginBottom:6}}>
              <div className="text-xs uppercase muted">Guarantor cheques</div>
              <div className="grow"/>
              <span className={`text-xs ${chequesOk ? 'pos' : 'muted'}`}>{fmtBHD(totalCheques)} / {fmtBHD(app.amount)} BHD {chequesOk && <I.Check s={11}/>}</span>
            </div>
            <div style={{border:'1px solid var(--line)',borderRadius:4}}>
              <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr 30px',gap:8,padding:'6px 10px',fontSize:11,color:'var(--ink-3)',background:'var(--bg-sunken)',textTransform:'uppercase',letterSpacing:'0.05em'}}>
                <span>Guarantor</span><span>Amount</span><span>Date</span><span>Cheque #</span><span>Bank</span><span/>
              </div>
              {cheques.map((c, i) => (
                <div key={i} style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr 30px',gap:8,padding:8,borderTop:'1px solid var(--line)'}}>
                  <input className="input" placeholder="Guarantor name" value={c.guarantorName} onChange={e => updateCheque(i, 'guarantorName', e.target.value)}/>
                  <input className="input font-mono" placeholder="0.000" value={c.amount} onChange={e => updateCheque(i, 'amount', e.target.value)}/>
                  <input className="input font-mono" type="date" value={c.chequeDate} onChange={e => updateCheque(i, 'chequeDate', e.target.value)}/>
                  <input className="input font-mono" placeholder="CHQ-…" value={c.chequeNumber} onChange={e => updateCheque(i, 'chequeNumber', e.target.value)}/>
                  <select className="select" value={c.bank} onChange={e => updateCheque(i, 'bank', e.target.value)}>
                    <option value="">— Select —</option>
                    {(QH_DATA.banks || []).filter(b => b.active).map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                  </select>
                  <button className="icon-btn" onClick={() => removeCheque(i)} disabled={cheques.length === 1}><I.X s={12}/></button>
                </div>
              ))}
              <button className="btn ghost sm" style={{margin:8}} onClick={addCheque}><I.Plus/> Add cheque</button>
            </div>
          </div>
          {err && <div style={{color:'var(--neg)',fontSize:12}}>{err}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <div style={{flex:1}}/>
          <button className="btn accent" onClick={submit} disabled={busy || !goldOk || !chequesOk}><I.Check/> {busy ? 'Approving…' : 'Confirm approval'}</button>
        </div>
      </div>
    </div>
  );
}

function RepaymentsPanel() {
  return (
    <div className="card">
      <div className="card-header">Upcoming & at-risk repayments <span className="actions"><span className="tag warn">{QH_DATA.repayments.filter(r => r.status === 'overdue' || r.status === 'bounced').length} attention</span></span></div>
      <div className="table-wrap" style={{border: 'none', borderRadius: 0}}>
        <table className="data">
          <thead><tr><th>Applicant</th><th>Due</th><th className="right">Amount</th><th>Cheque</th><th>Status</th><th>Cleared on</th><th></th></tr></thead>
          <tbody>
            {QH_DATA.repayments.map(r => (
              <RepaymentRow key={r.id} r={r}/>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RepaymentRow({ r }) {
  const [busy, setBusy] = useStateF(false);
  const [showClear, setShowClear] = useStateF(false);
  const canEdit = window.can?.('repayments', 'edit');
  const isRotated = !!r.rotatedToAppId;
  const isCleared = r.status === 'cleared';
  const isBounced = r.status === 'bounced';
  const bounce = async () => {
    if (!confirm(`Mark cheque ${r.cheque} as bounced? This creates a defaulter entry if none exists.`)) return;
    setBusy(true);
    try {
      await window.QH_API.post(`/repayments/${r.id}/mark-bounced`);
      await window.QH_STORE.reload();
    } catch (e) { alert(e.message || 'Failed.'); }
    finally { setBusy(false); }
  };
  const unclear = async () => {
    if (!confirm('Un-clear this repayment? It returns to pending.')) return;
    setBusy(true);
    try {
      await window.QH_API.post(`/repayments/${r.id}/unclear`);
      await window.QH_STORE.reload();
    } catch (e) { alert(e.message || 'Failed.'); }
    finally { setBusy(false); }
  };

  return (
    <tr>
      <td>{r.applicant}</td>
      <td className="font-mono text-xs">{r.dueDate}</td>
      <td className="num right" style={{fontWeight: 600}}>{fmtBHD(r.amount)}</td>
      <td className="font-mono text-xs muted">{r.cheque}</td>
      <td>
        <span className={`tag ${isCleared ? 'pos' : r.status === 'pending' ? 'info' : 'neg'}`}>{r.status}</span>
        {isRotated && <span className="tag" style={{marginLeft:4,textTransform:'none'}} title={`Rotated to ${r.rotatedToAppId}`}>rotated</span>}
      </td>
      <td className="font-mono text-xs">{r.clearedOn || '—'}</td>
      <td style={{whiteSpace:'nowrap',textAlign:'right'}}>
        {canEdit && !isCleared && !isBounced && !isRotated && (
          <>
            <button className="btn sm" onClick={() => setShowClear(true)} disabled={busy} title="Record cheque clearance"><I.Check s={11}/> Clear</button>
            <button className="btn sm ghost" style={{marginLeft:4}} onClick={bounce} disabled={busy} title="Mark as bounced"><I.X s={11}/> Bounce</button>
          </>
        )}
        {canEdit && isCleared && !isRotated && (
          <button className="btn sm ghost" onClick={unclear} disabled={busy} title="Revert to pending"><I.Refresh s={11}/> Un-clear</button>
        )}
        {isRotated && <span className="text-xs muted">cleared via rotation</span>}
      </td>
      {showClear && (
        <window.ClearChequeModal
          title="Mark cheque cleared"
          subtitle={`Repayment ${r.cheque} · ${r.applicant}`}
          cheque={{ issuer: r.applicant, cheque: r.cheque, bank: r.bank, dueDate: r.dueDate, amount: r.amount }}
          onClose={() => setShowClear(false)}
          onSubmit={async ({ clearedOn, depositBankId, comment }) => {
            await window.QH_API.post(`/repayments/${r.id}/mark-deposited`, { clearedOn, depositBankId, comment });
            await window.QH_STORE.reload();
          }}/>
      )}
    </tr>
  );
}

function DefaultersPanel() {
  return (
    <div className="card">
      <div className="card-header">Defaulters <span className="actions"><span className="tag neg">{QH_DATA.defaulters.length}</span></span></div>
      <div className="card-body" style={{padding: 0}}>
        {QH_DATA.defaulters.map((d, i) => (
          <div key={i} style={{padding: 14, borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 12}}>
            <div className="avatar lg" style={{background: 'var(--neg-soft)', color: 'var(--neg)'}}>{d.applicant.split(' ').map(x => x[0]).slice(0, 2).join('')}</div>
            <div style={{flex: 1}}>
              <div style={{fontWeight: 500}}>{d.applicant}</div>
              <div className="text-xs muted">{d.reason}</div>
              <div className="text-xs muted-2 font-mono" style={{marginTop: 2}}>Since {d.sinceDate} · {d.appId.toUpperCase()}</div>
            </div>
            <div style={{textAlign: 'right'}}>
              <div className="font-mono neg" style={{fontWeight: 600}}>{fmtBHD(d.dueAmount)}</div>
              {window.can('repayments','edit') && <button className="btn sm" style={{marginTop: 6}} onClick={async () => { if(!window.confirm('Mark this defaulter settled?')) return; try { await window.QH_API.post(`/finance/defaulters/${d.id}/settle`); await window.QH_STORE.reload(); } catch (e) { alert(e.message); } }}>Settle</button>}
            </div>
          </div>
        ))}
        <button className="btn ghost sm" style={{margin: 10}}>View all defaulters<I.ArrowRight s={11}/></button>
      </div>
    </div>
  );
}

// ============ Transaction modal (deposit / withdraw) ============
function TransactionModal({ schemeId, onClose, onCreated }) {
  const [type, setType] = useStateF('deposit');
  const [amount, setAmount] = useStateF('500.000');
  const [member, setMember] = useStateF(null); // {itsNo, fullName, ...}
  // Default to the first active bank — banks are now first-class entities
  // managed under Directory → Banks rather than a hardcoded list.
  const [bank, setBank] = useStateF(((QH_DATA.banks || []).find(b => b.active)?.name) || '');
  const [event, setEvent] = useStateF('');
  // Per-transaction maturity: Normal by default. Commitment requires months >= 1.
  const [matKind, setMatKind] = useStateF('normal');
  const [matMonths, setMatMonths] = useStateF('24');
  const [notify, setNotify] = useStateF({ whatsapp: true, email: false });
  const [busy, setBusy] = useStateF(false);
  const [err, setErr] = useStateF('');
  const sch = QH_DATA.schemes.find(x => x.id === schemeId);
  const events = QH_DATA.events.filter(e => e.schemeId === schemeId);

  const submit = async () => {
    if (!member?.itsNo) { setErr('Pick a member by ITS / CPR / name.'); return; }
    let commitmentMonths = null;
    if (type === 'deposit' && matKind === 'commitment') {
      const m = Number(matMonths);
      if (!Number.isFinite(m) || m < 1) { setErr('Commitment months must be at least 1.'); return; }
      commitmentMonths = m;
    }
    setBusy(true); setErr('');
    try {
      // Server now dispatches notifications based on the member's contact info
      // and the per-scheme template — the client just sets the flags.
      await window.QH_API.post('/transactions', {
        schemeId, eventId: event || null,
        memberItsNo: member.itsNo, memberName: member.fullName,
        type, amount: Number(amount), bank, commitmentMonths,
        notifyWhatsapp: notify.whatsapp, notifyEmail: notify.email,
      });
      await window.QH_STORE.reload();
      onCreated({ type, amount: Number(amount), member: member.fullName, bank });
      onClose();
    } catch (e) {
      setErr(e.message || 'Failed to record transaction.');
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h3>Record transaction</h3><button className="icon-btn close" onClick={onClose} style={{marginLeft: 'auto'}}><I.X/></button></div>
        <div className="modal-body" style={{display: 'flex', flexDirection: 'column', gap: 12}}>
          <div style={{display: 'flex', gap: 6}}>
            <button onClick={() => setType('deposit')} className={`btn ${type === 'deposit' ? 'accent' : ''}`} style={{flex: 1, justifyContent: 'center', height: 40}}><I.ArrowDown s={13}/> Deposit</button>
            <button onClick={() => setType('withdrawal')} className={`btn ${type === 'withdrawal' ? 'accent' : ''}`} style={{flex: 1, justifyContent: 'center', height: 40}}><I.ArrowUp s={13}/> Withdrawal</button>
          </div>
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
            <div className="field" style={{gridColumn: '1 / -1'}}><label>Scheme</label><input className="input font-mono" value={sch?.name + ' · ' + sch?.code} readOnly/></div>
            <div className="field" style={{gridColumn: '1 / -1'}}>
              <label>Member</label>
              <MemberPicker value={member} onChange={setMember} autoFocus/>
              {member && <div className="text-xs muted" style={{marginTop:4}}>ITS {member.itsNo} · {[member.cpr, member.mobile1, member.email1].filter(Boolean).join(' · ')}</div>}
            </div>
            <div className="field"><label>Event</label>
              <select className="select" value={event} onChange={e => {
                const next = e.target.value;
                setEvent(next);
                // Pre-select this event's default bank, if any. The user can
                // still pick a different bank afterwards.
                const ev = events.find(x => x.id === next);
                if (ev?.defaultBankName) setBank(ev.defaultBankName);
              }}>
                <option value="">— Select event —</option>
                {events.map(e => <option key={e.id} value={e.id}>{e.name}{e.defaultBankName ? ` · ${e.defaultBankName}` : ''}</option>)}
              </select>
            </div>
            <div className="field"><label>Amount (BHD)</label><input className="input font-mono" value={amount} onChange={e => setAmount(e.target.value)} style={{fontSize: 16, fontWeight: 600}}/></div>
            <div className="field"><label>Bank</label>
              <select className="select" value={bank} onChange={e => setBank(e.target.value)}>
                <option value="">— Select bank —</option>
                {(QH_DATA.banks || []).filter(b => b.active).map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
              </select>
              {(() => {
                const ev = events.find(x => x.id === event);
                if (ev?.defaultBankName && ev.defaultBankName === bank) {
                  return <div className="text-xs muted" style={{marginTop:4}}><I.Check s={11}/> Pre-selected from event default</div>;
                }
                if (ev?.defaultBankName && ev.defaultBankName !== bank) {
                  return <div className="text-xs" style={{color:'var(--warn)',marginTop:4}}><I.AlertCircle s={11}/> Event default is <b>{ev.defaultBankName}</b></div>;
                }
                return null;
              })()}
            </div>
          </div>
          {type === 'deposit' && (
            <div style={{padding: 12, background: 'var(--bg-sunken)', borderRadius: 4}}>
              <div className="text-xs uppercase muted" style={{marginBottom: 8}}>Maturity</div>
              <div style={{display: 'flex', gap: 16, alignItems: 'center'}}>
                <label style={{display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer'}}>
                  <input type="radio" name="matKind" checked={matKind === 'normal'} onChange={() => setMatKind('normal')}/>
                  Normal · no maturity
                </label>
                <label style={{display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer'}}>
                  <input type="radio" name="matKind" checked={matKind === 'commitment'} onChange={() => setMatKind('commitment')}/>
                  Commitment
                </label>
                {matKind === 'commitment' && (
                  <div style={{display: 'flex', alignItems: 'center', gap: 6, fontSize: 13}}>
                    <input className="input font-mono" style={{width: 70, height: 28}} value={matMonths} onChange={e => setMatMonths(e.target.value)}/>
                    <span className="muted">months (≥ 1)</span>
                  </div>
                )}
              </div>
            </div>
          )}
          <div style={{padding: 12, background: 'var(--bg-sunken)', borderRadius: 4}}>
            <div className="text-xs uppercase muted" style={{marginBottom: 8}}>Notifications</div>
            <div style={{display: 'flex', gap: 16}}>
              <label style={{display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer'}}>
                <span className={`check ${notify.whatsapp ? 'on' : ''}`} onClick={() => setNotify({...notify, whatsapp: !notify.whatsapp})}/>
                <I.MessageSquare s={13}/> Send WhatsApp confirmation
              </label>
              <label style={{display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer'}}>
                <span className={`check ${notify.email ? 'on' : ''}`} onClick={() => setNotify({...notify, email: !notify.email})}/>
                <I.Mail s={13}/> Send Email confirmation
              </label>
            </div>
          </div>
        </div>
        {err && <div style={{padding:'8px 14px',color:'var(--neg)',fontSize:12}}>{err}</div>}
        <div className="modal-footer">
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={submit} disabled={busy}><I.Check/> {busy ? 'Saving…' : `Confirm ${type}`}</button>
        </div>
      </div>
    </div>
  );
}

window.FinancePage = FinancePage;
window.TransactionModal = TransactionModal;
window.DefaultersPanel = DefaultersPanel;
window.RepaymentsPanel = RepaymentsPanel;
